import { SortList } from '@/components/custom-table-ag';
import { MessageResource } from '@/i18n/messages';
import {
    getCurrentDossierDetailProcess,
    getCurrentSummaryProcess,
    mergeDetailInto,
    sortBySendAt,
} from '@/app/[locale]/(root)/(MC)/dossier/_shared/dossier-detail-handler';
import { nameof, randomuuid, wrapQuote } from '@/lib/utils';
import { createZustandShallowStore } from '@/lib/zustand';
import { AssignTaskDetailResponse } from '@/server/api/v1/dossier-assign-task/type';
import { DossierPauseResponse } from '@/server/api/v1/dossier-pause/type';
import {
    DossierDetailProcessResponse,
    DossierDetailResponse,
    DossierPageResponse,
    DossierSummaryProcessesResponse,
} from '@/server/api/v1/dossier/type';
import { EmployeeSearchAdvanceResponse } from '@/server/api/v1/employee/type';
import {
    ChildLogicalOperator,
    FilterBuilders,
    LogicalOperator,
    OperatorType,
} from '@/server/base/base-trpc-service';
import {
    DossierDetailProcessingStatus,
    DossierPauseStatus,
    DossierPauseType,
    DossierSummaryProcessStatus,
    ForwardType,
} from '@/types/enum';
import { FuncResource, SetStoreState } from '@/types/use';
import { DossierFilterForm } from './dossier-advance-search';

export enum PageAction {
    // TODO: Các nút nằm trong thanh công cụ
    /**Tiếp nhận hồ sơ */
    recv = 'DossierManagement_Receive',
    /**Tiếp nhận bổ sung hồ sơ */
    recv_additional = 'DossierManagement_ReceiveAdditional',
    /**Tiếp nhận nhiều online */
    recvMany = 'DossierManagement_ReceiveMany',
    /**Chuyển hồ sơ */
    transfer = 'DossierManagement_Transfer',
    /**In biên bản bàn giao */
    handover = 'DossierManagement_PrintHandoverRecord',
    /**Xóa hồ sơ */
    del = 'DossierManagement_Delete',
    /**Giao trả nhiều hồ sơ */
    returnMany = 'DossierManagement_ReturnMany',
    /**
     * Duyệt & Giao trả nhiều hồ sơ
     */
    finishAndReturnMany = 'DossierManagement_FinishAndReturnMany',

    // TODO: Các nút ở trong bảng danh sách hồ sơ
    /**Nút xử lý chi tiết hồ sơ */
    processing = 'DossierManagement_Processing',
    /**Nút sửa thông tin hồ sơ */
    edit = 'DossierManagement_Edit',
    /**Nút in biên nhận */
    printReceipt = 'DossierManagement_PrintReceipt',
    /**Nút thông tháo người học */
    send_notice = 'DossierManagement_SendNotice',
    /**Nút in quyết định */
    print_decision = 'DossierManagement_PrintDecision',
   
    /**Nút duyệt hồ sơ */
    approve = 'DossierManagement_ApproveDossier',
    /**Gửi nhắc việc */
    send_remind = 'DossierManagement_SendRemind',
    /**In quyết định hàng loạt (thanh công cụ) */
    print_decision_many = 'DossierManagement_PrintDecisionMany',
    /**Chuyển hàng loạt (thanh công cụ) */
    transfer_many = 'DossierManagement_TransferMany',
    /**Tiếp nhận hoàn thành hàng loạt (thanh công cụ) */
    recvCompletedMany = 'DossierManagement_ReceiveCompletedMany',
    /**Trả hồ sơ hàng loạt (thanh công cụ) - gọi API ReturnResults */
    returnResultsMany = 'DossierManagement_ReturnResultsMany',
}

/**Tiêu chí tìm kiếm theo menu */
export enum SearchMenuType {
    GetAll = 'all',
    ByMenu = 'menu',
}

export enum TableColFilterQuery {
    EmptySendInfo = 'empty-send-info',
    Step = 'step',
}

type TaskHandle = {
    type: 'detail' | 'summary' | 'pause' | 'assign-task';
    send_at: string;

    /**Nếu không có empIds ==> Đây là task mới nhất của tất cả nhân viên */
    empIds?: string[];
    data: any;
};

type DossierManagementStoreProps = {
    /**Đang lấy thông tin nhân viên  */
    isFetchingDirectoryEmpInfo: boolean;

    funcResources: FuncResource<PageAction> | undefined;
    directoryEmpInfo: Record<string, EmployeeSearchAdvanceResponse | undefined>;

    setFuncResouces: SetStoreState<FuncResource<PageAction> | undefined>;
    /**Hàm set các state trong store này
     * @description VUI LÒNG SÀI CẨN THẬN. COI CHỪNG BỊ GHI ĐÈ LÊN STATE CŨ
     */
    setStoreState: SetStoreState<
        Partial<DossierManagementStoreProps> | undefined
    >;

    changedDossierIds: string[] | undefined;
    setChangedDossierIds: (ids: string[] | undefined) => void;
    randomChangedDossierId: () => void;

};

/**Store của các trang danh sách hồ sơ */
export const useDossierManagementStore =
    createZustandShallowStore<DossierManagementStoreProps>((set, get) => {
        return {
            isFetchingDirectoryEmpInfo: false,
            funcResources: undefined,
            directoryEmpInfo: {},

            setFuncResouces(fn) {
                set((st) => {
                    return {
                        funcResources:
                            typeof fn === 'function'
                                ? fn(st.funcResources)
                                : fn,
                    };
                });
            },
            setStoreState(fn) {
                set((st) => {
                    const _obj = typeof fn === 'function' ? fn(st) : fn;

                    return {
                        ..._obj,
                    };
                });
            },
            changedDossierIds: undefined,
            setChangedDossierIds(ids: string[] | undefined) {
                set({
                    changedDossierIds: ids
                });
            },
            randomChangedDossierId() {
                set({
                    changedDossierIds: [randomuuid()]
                });
            },
        };
    });

export const onSearch = (keyword: string, extFilter?: DossierFilterForm) => {
    let filters: FilterBuilders<DossierPageResponse>[] = [];

    if (extFilter?.procedure) {
        filters.push({
            key: nameof<DossierPageResponse>('ProcedureCode'),
            values: [extFilter.procedure],
            logicalOperator: LogicalOperator.And,
            operatorType: OperatorType.Equal,
        });
    }

    if (extFilter?.handoverMethod && extFilter.handoverMethod.length) {
        filters.push({
            key: nameof<DossierPageResponse>('Handover_Method'),
            values: [parseInt(extFilter.handoverMethod)],
            logicalOperator: LogicalOperator.And,
            operatorType: OperatorType.Equal,
        });
    }

    if (extFilter?.typeToken && extFilter.typeToken.length) {
        const wrap = wrapQuote(extFilter.typeToken);
        if (wrap && wrap.length) {
            filters.push({
                key: nameof<DossierPageResponse>('ProcudureTypes_Token'),
                values: [wrap],
                logicalOperator: LogicalOperator.And,
                operatorType: OperatorType.Regex,
            });
        }
    }

    if (extFilter?.fieldCode && extFilter.fieldCode.length) {
        const wrap = wrapQuote(extFilter.fieldCode);
        if (wrap && wrap.length) {
            filters.push({
                key: nameof<DossierPageResponse>('FieldCodes_Token'),
                values: [wrap],
                logicalOperator: LogicalOperator.And,
                operatorType: OperatorType.Regex,
            });
        }
    }

    if (keyword.length) {
        filters.push({
            // TODO: hàm filter page thì phải có key để detect
            key: nameof<DossierPageResponse>('Customer_Name'),
            logicalOperator: LogicalOperator.And,
            childLogicalOperator: ChildLogicalOperator.Or,
            filters: [
                {
                    key: nameof<DossierPageResponse>('Customer_Name'),
                    values: [keyword],
                    logicalOperator: LogicalOperator.Or,
                    operatorType: OperatorType.Regex,
                },

                {
                    key: nameof<DossierPageResponse>('StudentID'),
                    values: [keyword],
                    logicalOperator: LogicalOperator.Or,
                    operatorType: OperatorType.Regex,
                },

                {
                    key: nameof<DossierPageResponse>('Summary'),
                    values: [keyword],
                    logicalOperator: LogicalOperator.Or,
                    operatorType: OperatorType.Regex,
                },

                {
                    key: nameof<DossierPageResponse>('Receiving_Number'),
                    values: [keyword],
                    logicalOperator: LogicalOperator.Or,
                    operatorType: OperatorType.Regex,
                },
            ],
        });
    }

    return filters;
};

/**
 * Hàm thực hiện kiểm tra và lấy ra công việc mới nhát của nhân viên
 */
export function getLatestTask({
    dossierSummaryProcesses,
    empId,
    unitCode,
    dossierItem,
    isGetLatestOfEmp = true,
}: {
    /**@description Hồ sơ chi tiết  không cần truyền */
    dossierItem?: DossierDetailResponse;
    /**@description Quy trình tổng hợp */
    dossierSummaryProcesses?: DossierSummaryProcessesResponse[];
    /**@description ID nhân viên */
    empId?: string;
    /**@description Mã đơn vị */
    unitCode?: string;
    /**@description Lấy thông tin xử lý mới nhất của hồ sơ hay thông tin xử lý mới nhất của người đang đăng nhập */
    isGetLatestOfEmp?: boolean;
}) {
    const objTask: {
        summary?: DossierSummaryProcessesResponse;
        detailProcess?: DossierDetailProcessResponse;
        assignTask?: AssignTaskDetailResponse;
        pauseData?: DossierPauseResponse;
    } = {};

    let tasks: TaskHandle[] = [];

    const currentSummaryProcess = getCurrentSummaryProcess({
        data: dossierSummaryProcesses,
        unitCode: unitCode,
        dossier: dossierItem,
    });

    if (currentSummaryProcess) {
        // Lấy ra chi tiết xử lý hiện tại
        const currentDetailProcessOfEmp = getCurrentDossierDetailProcess({
            empId: empId,
            data: currentSummaryProcess.DossierDetailProcesses,
        });

        const latestDetailProcess =
            currentSummaryProcess.DossierDetailProcesses?.findLast(
                (x) =>
                    x.ProcessingStatus === DossierDetailProcessingStatus.Pending
            );

        if (currentDetailProcessOfEmp) {
            let _dateStr = currentDetailProcessOfEmp.Send_At;

            // Gắn nó về cuối khi sắp xếp
            if (_dateStr) {
                const _date = new Date(_dateStr);
                _date.setUTCSeconds(50);
                _dateStr = _date.toISOString();
            }

            tasks.push({
                type: 'detail',
                send_at: _dateStr,
                data: currentDetailProcessOfEmp,
                empIds: [currentDetailProcessOfEmp.EmployeeId],
            });
        }

        if (latestDetailProcess) {
            let _dateStr = latestDetailProcess.Send_At;

            // Gắn nó về cuối khi sắp xếp
            if (_dateStr) {
                const _date = new Date(_dateStr);
                _date.setUTCSeconds(59);
                _dateStr = _date.toISOString();
            }

            tasks.push({
                type: 'detail',
                send_at: _dateStr,
                data: latestDetailProcess,
                empIds: [latestDetailProcess.EmployeeId],
            });
        }

        /**Có thông tin xử lý  */
        const arrCoordination = dossierSummaryProcesses
            ?.filter((x) => {
                return (
                    (x.Type === ForwardType.Connect ||
                        x.Type === ForwardType.AskAdvice) &&
                    x.Status !== DossierSummaryProcessStatus.Cancel &&
                    x.Status !== DossierSummaryProcessStatus.Refuse
                );
            })
            .map(mergeDetailInto);

        if (arrCoordination?.length) {
            arrCoordination.forEach((coor) => {
                if (coor) {
                    tasks.push({
                        type: 'summary',
                        send_at: coor.Send_At,
                        data: coor,
                        empIds: [coor.Send_EmployeeId, coor.EmployeeId],
                    });
                }
            });
        }

        if (currentSummaryProcess.AssignTasks?.length) {
            currentSummaryProcess.AssignTasks.forEach((task) => {
                const mergeTask = mergeDetailInto(task);
                if (mergeTask) {
                    tasks.push({
                        type: 'assign-task',
                        send_at: mergeTask.Send_At,
                        data: mergeTask,
                        empIds: [mergeTask.EmployeeId],
                    });
                }
            });
        }

        /**
         * Lấy dossierPause
         */
        const dossierPause = currentSummaryProcess.DossierPauses?.findLast(
            (pause) => {
                const mergePause = mergeDetailInto(pause);
                if (
                    currentSummaryProcess.Pause_IsExist &&
                    currentSummaryProcess.Pause_Id
                ) {
                    return mergePause?.ID === currentSummaryProcess.Pause_Id;
                }

                return (
                    mergePause?.SummaryProcessCode ===
                    currentSummaryProcess.Code
                );
            }
        );

        if (dossierPause) {
            let empIds: string[] | undefined;

            if (
                dossierPause.Status === DossierPauseStatus.Additional &&
                dossierPause.Type === DossierPauseType.Request
            ) {
                empIds = [
                    dossierPause.DecidedBy_EmployeeId,
                    dossierPause.OtherRelated_EmployeeId,
                    dossierPause.RequestBy_EmployeeId,
                    dossierPause.FinishedBy_EmployeeId,
                ];
            }
            if (dossierPause.Status === DossierPauseStatus.Other) {
                empIds = [
                    dossierPause.OtherRelated_EmployeeId,
                    dossierPause.RequestBy_EmployeeId,
                ];
            }

            tasks.push({
                type: 'pause',
                send_at: dossierPause?.Detail?.ChangeTypeAt ?? '',
                data: dossierPause,
                empIds: empIds,
            });
        }
    }

    if (tasks.length) {
        tasks = sortBySendAt(tasks, 'send_at');

        /**Công việc mới nhất của nhân viên */
        let latestTask = tasks.findLast((x) => {
            return !x.empIds || (x.empIds && x.empIds.includes(empId!));
        });

        if (!isGetLatestOfEmp) {
            latestTask = tasks[tasks.length - 1];
        }

        if (latestTask) {
            switch (latestTask.type) {
                case 'detail':
                    objTask.detailProcess =
                        latestTask.data as DossierDetailProcessResponse;
                    break;
                case 'summary':
                    objTask.summary =
                        latestTask.data as DossierSummaryProcessesResponse;
                    break;
                case 'assign-task':
                    objTask.assignTask =
                        latestTask.data as AssignTaskDetailResponse;
                    break;
                case 'pause':
                    objTask.pauseData = latestTask.data as DossierPauseResponse;
                    break;
            }
        }
    }

    return objTask;
}

/**các sort key của table */
export const getSortList = (
    t: (data: keyof MessageResource['DossierManagement']) => string,
    transDef: (data: keyof MessageResource['System']) => string
): SortList => [
        {
            label: t('DossierManagement_LatestProcessing'),
            value: nameof<DossierPageResponse>('SortTimeStamp'),
        },
        {
            label: t('DossierManagement_Submission_Date'),
            value: nameof<DossierPageResponse>('Submission_Date'),
        },
        {
            label: t('DossierManagement_ReceivedDate'),
            value: nameof<DossierPageResponse>('Receiving_Date'),
        },
    ];

/**
 * Chuyển data từ getPage thành getDetail
 * @param data
 * @returns
 */
export function convertDossierPageToDetail(
    data: DossierPageResponse
): DossierDetailResponse {
    const obj: DossierDetailResponse = {
        ...data.Detail,
        Detail: data.Detail,
        DossierSummaryProcesses: data.DossierSummaryProcesses,
        DossierFees: data.DossierFees,
        Procedure: data.Procedure || data.Detail.Procedure,
    };

    return obj;
}
